'use client'

import { useState } from 'react'
import NextImage from 'next/image'
import { ActionIcon, Box, Group, Image, Loader, SimpleGrid, Stack, Text } from '@mantine/core'
import { faFilePdf } from '@fortawesome/pro-light-svg-icons'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatDate, redactAttributes, redactMediaProps, redactPropsWithClassName } from '@phoenix-web/utils/general'
import { type UploadedDealFile, type UploadedDealFiles } from '@phoenix-web/ux-gql/queries'
import PhotoCarousel from './PhotoCarousel'
import { ACCEPTED_PHOTO_TYPES } from './PhotoUploader'
import type { AcceptedPhotoTypes } from './PhotoUploader'

// Custom image component with loading state
function ImageWithLoader({
  src,
  alt,
  width,
  height,
  style,
  onClick,
}: {
  src: string
  alt: string
  width: number
  height: number
  style?: React.CSSProperties
  onClick?: () => void
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  return (
    <Box style={{ position: 'relative' }}>
      {isLoading && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--mantine-color-gray-1)',
            borderRadius: '3px 3px 0 0',
            zIndex: 1,
          }}
        >
          <Loader size="sm" color="gray" />
        </Box>
      )}

      {hasError && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--mantine-color-gray-1)',
            borderRadius: '3px 3px 0 0',
            zIndex: 1,
          }}
        >
          <Text size="xs" c="dimmed">
            Failed to load
          </Text>
        </Box>
      )}

      <Image
        {...redactMediaProps}
        component={NextImage}
        width={width}
        height={height}
        src={src}
        alt={alt}
        fit="cover"
        style={{
          ...style,
          opacity: isLoading || hasError ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out',
        }}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false)
          setHasError(true)
        }}
        onClick={onClick}
      />
    </Box>
  )
}

// Small reusable trash overlay shown on a file/photo card when deletion is enabled.
function DeleteOverlayButton({
  file,
  onDelete,
}: {
  file: UploadedDealFile
  onDelete: (file: UploadedDealFile) => void
}) {
  return (
    <ActionIcon
      {...redactAttributes('aria-label')}
      aria-label={`Delete ${file.file_name}`}
      color="red"
      variant="filled"
      style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
      onClick={(e) => {
        // Prevent the card click (which opens the carousel) from firing.
        e.stopPropagation()
        onDelete(file)
      }}
    >
      <FontAwesomeIcon icon={faTrash} />
    </ActionIcon>
  )
}

export default function PhotoViewer({
  photos,
  showEmployeeUploaderNames = false,
  onDeletePhoto,
}: {
  photos: UploadedDealFiles
  showEmployeeUploaderNames?: boolean
  /**
   * When provided, a delete icon is rendered on each file/photo card. The
   * callback receives the file to delete; the consumer owns confirmation,
   * the mutation, and any list refresh. Omit to hide the delete affordance
   * (e.g. in customer-facing contexts).
   */
  onDeletePhoto?: (file: UploadedDealFile) => void
}) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null)
  const [selectedPdfIndex, setSelectedPdfIndex] = useState<number | null>(null)

  const photoFiles =
    photos?.filter((file) => {
      return ACCEPTED_PHOTO_TYPES.includes(file?.content_type as AcceptedPhotoTypes)
    }) || []

  const nonPhotoFiles =
    photos?.filter((file) => {
      return !ACCEPTED_PHOTO_TYPES.includes(file?.content_type as AcceptedPhotoTypes)
    }) || []

  return (
    <>
      {(photoFiles?.length === 0 || !photoFiles) && (nonPhotoFiles?.length === 0 || !nonPhotoFiles) ? (
        <Stack>
          <Text fw="bold" fz="lg" pt="3rem">
            No photos have been uploaded for this property yet.
          </Text>
        </Stack>
      ) : (
        <>
          {/* Start: Photos section */}
          {photoFiles.length > 0 && (
            <Stack>
              <Group>
                <Text ta="right" w={'100%'} fw="bold">
                  {photoFiles.length} {photoFiles.length === 1 ? 'Photo' : 'Photos'}
                </Text>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="xxl" verticalSpacing="3rem">
                {photoFiles?.map((photo, index) => (
                  <Box key={photo.file_upload_id} style={{ position: 'relative' }}>
                    {onDeletePhoto && <DeleteOverlayButton file={photo} onDelete={onDeletePhoto} />}
                    <Stack
                      gap="0"
                      bd="1px solid gray.5"
                      style={{ borderRadius: 'var(--mantine-radius-sm)', cursor: 'pointer' }}
                      onClick={() => setSelectedPhotoIndex(index)}
                    >
                      <ImageWithLoader
                        src={photo.presigned_url}
                        alt={photo.file_name}
                        width={250}
                        height={250}
                        style={{
                          borderRadius: '3px 3px 0 0',
                          maxWidth: '100%',
                        }}
                      />
                      <Stack p="sm" gap="xs">
                        <Text fw="bold" size="md" lineClamp={1} {...redactPropsWithClassName}>
                          {photo.file_name}
                        </Text>
                        <Group align="center" justify="space-between">
                          <Text size="xs" c="brand-blue.9" opacity={0.8} lineClamp={1} {...redactPropsWithClassName}>
                            Uploaded by:{' '}
                            {photo.uploader_is_employee && !showEmployeeUploaderNames
                              ? 'Knock Team'
                              : `${photo.uploader_first_name} ${photo.uploader_last_name}`}
                          </Text>
                          <Text size="xs" c="brand-blue.9" opacity={0.8}>
                            {photo.uploaded_date ? formatDate(photo.uploaded_date.substring(0, 10), 'MM/dd/yyyy') : ''}
                          </Text>
                        </Group>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </SimpleGrid>

              <PhotoCarousel
                photos={photoFiles}
                initialIndex={selectedPhotoIndex ?? 0}
                opened={selectedPhotoIndex !== null}
                onClose={() => setSelectedPhotoIndex(null)}
              />
            </Stack>
          )}
          {/* End: Photos section */}

          {/* Start: Files section */}
          {nonPhotoFiles.length > 0 && (
            <Stack pt={photoFiles.length > 0 ? '3rem' : '0'}>
              <Text w={'100%'} fw="bold">
                {nonPhotoFiles.length} {nonPhotoFiles.length === 1 ? 'File' : 'Files'}
              </Text>

              <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing="xl" verticalSpacing="3rem">
                {nonPhotoFiles?.map((pdf, index) => (
                  <Box key={pdf.file_upload_id} style={{ position: 'relative' }}>
                    {onDeletePhoto && <DeleteOverlayButton file={pdf} onDelete={onDeletePhoto} />}
                    <Stack
                      gap="0"
                      bd="1px solid gray.5"
                      style={{ borderRadius: 'var(--mantine-radius-sm)', cursor: 'pointer' }}
                      onClick={() => setSelectedPdfIndex(index)}
                    >
                      <Box
                        style={{
                          position: 'relative',
                          height: 120,
                          background: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FontAwesomeIcon icon={faFilePdf} size="2x" />
                      </Box>
                      <Box
                        style={{
                          borderTop: '1px solid #D3D9DD', // or another valid color
                          borderRadius: '0 0 3px 3px',
                        }}
                        p="sm"
                      >
                        <Text fw="bold" size="xs" style={{ wordBreak: 'break-word' }} {...redactPropsWithClassName}>
                          {pdf.file_name}
                        </Text>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </SimpleGrid>

              <PhotoCarousel
                photos={nonPhotoFiles}
                initialIndex={selectedPdfIndex ?? 0}
                opened={selectedPdfIndex !== null}
                onClose={() => setSelectedPdfIndex(null)}
              />
            </Stack>
          )}
          {/* End: Files section */}
        </>
      )}
    </>
  )
}
